import {
  Box,
  Columns,
  DatePicker,
  Icon,
  Inline,
  Scrollable,
  Table,
  TableBody,
  Text,
} from '@lightspeed/design-system-react'
import * as React from 'react'
import { useQuery } from '@apollo/client'
import { useT } from '@transifex/react'
import { getLocaleForFormatDate } from '@vend/utilities'
import { SCHEDULE_INPUT_BOX_TYPE } from '../types'
import { DATE_SELECTOR_RANGE, MIN_HOURS_AND_MINUTES } from '../../constants'
import { useRegisterManager } from '../../../../../../ng-compat/hooks'
import { generateDate } from '../../../../../../util/agenda-utils'
import { ServiceAgenda } from '../../../../../../models/sale.constants'
import {
  AgendaData,
  GetAgendaVariables,
  getAgenda,
} from '../../../../../../queries/services'
import { getInitialStartDateForRange } from '../../utilities'
import { SelectADateHeadCell } from './SelectADateHeadCell'
import { SelectADateRow } from './SelectADateRow'
import { ServiceSchedulingInput } from './ServiceSchedulingInput'
interface Props {
  setValidationError: (error: string[]) => void
  validationError: string[]
  onServiceAgendaChange: (serviceAgenda: ServiceAgenda) => void
  serviceAgenda?: ServiceAgenda
  selectDateError: string | null
  shouldDisableCreateService: boolean
  selectedDate?: string
  setSelectedDate: (value: string) => void
  setHoursInput: (value: number) => void
  hoursInput: number | null
  setMinutesInput: (value: number) => void
  minutesInput: number | null
}

export const ServiceScheduling: React.FC<Props> = ({
  setValidationError,
  validationError,
  onServiceAgendaChange,
  serviceAgenda,
  selectDateError,
  shouldDisableCreateService,
  selectedDate,
  setSelectedDate,
  setHoursInput,
  setMinutesInput,
  hoursInput,
  minutesInput,
}) => {
  const t = useT()

  const serviceSchedulingCopies = {
    scheduleSubtitle: t('Enter how long the service will take', {
      _context: 'Subtitle for scheduling step in service modal.',
    }),
    selectDateSubtitle: t('Select a date', {
      _context: 'Subtitle for scheduling step in service modal.',
    }),
    hours: t(`Hours`, {
      _context: 'Label for scheduling step in service modal.',
    }),
    minutes: t(`Minutes`, {
      _context: 'Label for scheduling step in service modal.',
    }),
  }
  const registerManager = useRegisterManager()
  const {
    id: outletId,
    time_zone: timezone,
  } = registerManager.getActiveOutlet()

  const todayDate = new Date(new Date().setHours(0, 0, 0, 0))
  const twoYearsFromNowDate = new Date(
    new Date().setFullYear(new Date().getFullYear() + 2)
  )

  // Need to save the initial selected date to display it at the top of the date table (when editing a service about to be created)
  // and not let it be affected by the user's interaction with the dates table.
  const initialSelectedDate = React.useRef<string>(
    selectedDate ?? todayDate.toISOString()
  )

  const onHoursError = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValidationError([...validationError, SCHEDULE_INPUT_BOX_TYPE.HOURS])
  }

  const onMinutesError = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValidationError([...validationError, SCHEDULE_INPUT_BOX_TYPE.MINUTES])
  }
  const onHoursChange = (val: number) => {
    const removeHoursError = validationError.filter(
      err => err !== SCHEDULE_INPUT_BOX_TYPE.HOURS
    )
    setValidationError(removeHoursError)
    setHoursInput(val)
  }

  const onMinutesChange = (val: number) => {
    const removeMinutesError = validationError.filter(
      err => err !== SCHEDULE_INPUT_BOX_TYPE.MINUTES
    )
    setValidationError(removeMinutesError)
    setMinutesInput(val)
  }

  const [totalMinutesScheduled, setTotalMinutesScheduled] = React.useState(
    serviceAgenda?.minutesScheduled || MIN_HOURS_AND_MINUTES
  )

  const [agendaData, setAgendaData] = React.useState<ServiceAgenda[]>([])

  React.useEffect(() => {
    onServiceAgendaChange({
      date: selectedDate || '',
      minutesScheduled: totalMinutesScheduled,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalMinutesScheduled, selectedDate])

  React.useEffect(() => {
    const hoursInputCondition = !hoursInput ? 0 : hoursInput
    const minutesInputCondition = !minutesInput ? 0 : minutesInput
    setTotalMinutesScheduled(hoursInputCondition * 60 + minutesInputCondition)
  }, [hoursInput, minutesInput, shouldDisableCreateService])

  const [startDate, setStartDate] = React.useState<string | null>(null)

  const handleDatePickerChange = (date: Date) => {
    setStartDate(date.toISOString())
    // If the user has selected a date, reset the selected date to the new date when the date picker is interacted with.
    // This ensures the new date will be displayed at the top of the date table.
    if (selectedDate) {
      setSelectedDate(date.toISOString())
    }
  }
  const { data: agendaDataSuccess } = useQuery<AgendaData, GetAgendaVariables>(
    getAgenda,
    {
      fetchPolicy: 'no-cache',
      variables: {
        outletId,
        range: {
          startDate: getInitialStartDateForRange({
            initialSelectedDate: initialSelectedDate.current,
            startDate,
          }),
          rangeInDays: DATE_SELECTOR_RANGE,
          timezone,
        },
      },
    }
  )
  const initOutletAgenda = (): ServiceAgenda[] => {
    const outletAgenda: ServiceAgenda[] = []
    for (let i = 0; i < DATE_SELECTOR_RANGE; i++) {
      const currentDate = generateDate(i)
      const agendaItem = {
        date: currentDate,
        minutesScheduled: 0,
      }
      outletAgenda.push(agendaItem)
    }
    return outletAgenda
  }

  React.useEffect(() => {
    setAgendaData(initOutletAgenda())
  }, [])

  React.useEffect(() => {
    if (agendaDataSuccess) {
      const outletAgenda = agendaDataSuccess.getAgenda?.dates || []
      setAgendaData(outletAgenda)
    }
  }, [agendaDataSuccess])

  const dateLocale = getLocaleForFormatDate()

  return (
    <>
      <Box className="vd-pb3">
        <Text tag="p" type="signpost">
          {serviceSchedulingCopies.scheduleSubtitle}
        </Text>
      </Box>
      <Columns align="left" space={3}>
        <ServiceSchedulingInput
          onError={onHoursError}
          onChange={onHoursChange}
          label={SCHEDULE_INPUT_BOX_TYPE.HOURS}
          inputValue={hoursInput}
        />
        <ServiceSchedulingInput
          onError={onMinutesError}
          onChange={onMinutesChange}
          label={SCHEDULE_INPUT_BOX_TYPE.MINUTES}
          inputValue={minutesInput}
        />
      </Columns>
      <Box className="vd-pt5 vd-pb3">
        <Text tag="p" type="signpost">
          {serviceSchedulingCopies.selectDateSubtitle}
        </Text>
        {shouldDisableCreateService && (
          <Box className="vd-pt2 vd-pb2">
            <Inline
              align="left"
              space={2}
              className="vd-banner vd-banner--negative vd-pa4"
            >
              <Icon icon={'fa-exclamation-triangle'} />
              <Text
                type="label"
                wrap="break-word"
                data-testid="schedule-service-validation-error"
              >
                {selectDateError}
              </Text>
            </Inline>
          </Box>
        )}
        <>
          {/* The DatePicker is used to set the start date in the `range` for the
        table of dates. */}
          <Box className="vd-field--short vd-pt5 vd-pb5">
            <Text type="label">
              {t('View dates starting on', {
                _context: 'Label for date picker ',
              })}
            </Text>
            <Box data-testId="service-date-picker">
              <DatePicker
                locale={dateLocale}
                date={startDate ? new Date(startDate) : undefined}
                minDate={todayDate}
                maxDate={twoYearsFromNowDate} // 2 years from now - business decision
                onChange={handleDatePickerChange}
              />
            </Box>
          </Box>
        </>
        <Scrollable height={320}>
          <Table className="vd-table vd-table--compact" fixed={true}>
            <SelectADateHeadCell />
            <TableBody>
              {agendaData &&
                agendaData.map((dayAgenda, index) => {
                  return (
                    <SelectADateRow
                      key={index}
                      selectedDate={selectedDate}
                      setSelectedDate={setSelectedDate}
                      totalMinutesScheduled={totalMinutesScheduled}
                      agenda={(dayAgenda as unknown) as ServiceAgenda}
                    />
                  )
                })}
            </TableBody>
          </Table>
        </Scrollable>
      </Box>
    </>
  )
}
